import {Map as olMap, Tile, View as olView} from 'ol'
import {Zoom as olZoom, ScaleLine} from 'ol/control'
import {Tile as ollayerTile} from 'ol/layer'
import { Coordinate } from 'ol/coordinate';
import { Extent, getCenter, extend } from 'ol/extent';
import { transformExtent } from 'ol/proj';
import Point from 'ol/geom/Point';
import LineString from 'ol/geom/LineString';
import Style from 'ol/style/Style';
import { getLength } from 'ol/sphere'
import MapInstanceState from './mapinstancestate';
import ContextMenuControl from './ContextMenuControl';
import { Stroke } from 'ol/style';
import { Styles } from './Styles';
import { Units } from 'ol/control/ScaleLine';
import TrackVectorLayer from './TrackVectorLayer';
import ZoomToAllTracks from './ZoomToAllTracks';
import { defaults } from 'ol/interaction';
import TileSource from 'ol/source/Tile';

export const latLngProjection = "EPSG:4326";
export abstract class Renderer<TileSourceType extends TileSource> {
  abstract map: olMap;
  abstract projection: string;
  abstract layer: ollayerTile<TileSourceType>;
  type: string;
  abstract CreateView(centre: Coordinate): olView;
  
  constructor(type: string) {
    this.type = type
  }

  GetType() {
    return this.type
  }
  contextMenuControl: ContextMenuControl = new ContextMenuControl()

  scaleControl = new ScaleLine({ units: Units.IMPERIAL });
  setScaleLineUnit(distanceUnit: string) {
      this.scaleControl.setUnits(distanceUnit == "mi" ? Units.IMPERIAL : Units.METRIC)
  }

  GetTrackStyleForColour(colour: string): Style[] {
    const outer = new Style({
      stroke: new Stroke({
          color: 'black',
          width: 6
        })
      })
      const inner = new Style({
          stroke: new Stroke({
              color: colour,
              width: 5
          })
      })
      return [outer, inner]
  }

  ContextMenuLatLngCoordinates(): Coordinate|undefined {
    if(this.contextMenuControl.coordinate === undefined) {
      return undefined
    }
    return this.TransformPointToLatLngCoordinates(new Point(this.contextMenuControl.coordinate))
  }

  GetDefaultTrackStyle(trackType: string) {
    return trackType === "route" ? 
      this.GetTrackStyleForColour(Styles.DefaultRouteColour) : 
      this.GetTrackStyleForColour(Styles.DefaultTrackColour)
  }
  
  mapInstanceState = new MapInstanceState()
  
  Extent(): Extent {
    const extent = this.map.getView().calculateExtent();
    return transformExtent(extent, this.projection, latLngProjection);
  }

  Centre(): Coordinate {
    const centre = this.map.getView().getCenter()!;
    const centrePoint = new Point(centre);
    centrePoint.transform(this.projection, latLngProjection);
    return centrePoint.getCoordinates();
  }

  SetLatLngCentre(latLngCoord: Coordinate) {
    const coord = this.TransformCoordFromLatLng(latLngCoord)
    this.map.getView().setCenter(coord)
  }

  Dispose() {
      this.map.dispose()
      this.layer.dispose()
  }

  CreateMap(element: string, extentLatLng: Extent): olMap {
    const extent = transformExtent(extentLatLng, latLngProjection, this.projection);
    const centre = getCenter(extent);

    // Initialize the map object.
    const map = new olMap({
      layers: [this.layer],
      target: element,
      view: this.CreateView(centre),
      controls: [
        new olZoom({className: "zoom-control"}), 
        this.contextMenuControl,
        new ZoomToAllTracks(this),
        this.scaleControl
      ],
      interactions: defaults({pinchRotate: false})
    });

    const resolution = map.getView().getResolutionForExtent(extent);
    map.getView().setResolution(resolution);
    return map;
  }
  
  GetSize(): number {
    const extent = this.map.getView().calculateExtent()
    const corner1: Coordinate = [extent[0], extent[1]]
    const corner2: Coordinate = [extent[2], extent[3]]
    const line = new LineString([corner1, corner2])
    return getLength(line, {projection: this.projection})
  }

  TransformPointToLatLng(point: Point): Point {
      return new Point(this.TransformPointToLatLngCoordinates(point))
  }

  TransformPointToLatLngCoordinates(point: Point): Coordinate {
    return this.TransformCoordToLatLng(point.getCoordinates());
  }

  TransformPointFromLatLng(point: Point): Point {
      return new Point(this.TransformCoordFromLatLng(point.getCoordinates()))
  }

  TransformLineStringToLatLng(lineString: LineString): LineString {
    const newLineString = new LineString(lineString.getCoordinates())
    return newLineString.transform(this.projection, latLngProjection) as LineString
  }

  TransformLineStringToMetric(lineString: LineString): LineString {
    const newLineString = new LineString(lineString.getCoordinates())
    const transformedLineString = newLineString.transform(this.projection, latLngProjection) as LineString
    return transformedLineString
  }

  TransformLineStringFromLatLng(lineString: LineString): LineString {
    const newLineString = new LineString(lineString.getCoordinates())
    const transformedLineString = newLineString.transform(latLngProjection, this.projection) as LineString
    return transformedLineString
  }

  TransformCoordToLatLng(coord: Coordinate): Coordinate {
    const point = new Point(coord)
    point.transform(this.projection, latLngProjection)
    return point.getCoordinates()
  }

  TransformCoordFromLatLng(coord: Coordinate): Coordinate {
    const point = new Point(coord)
    point.transform(latLngProjection, this.projection)
    return point.getCoordinates()  
  }

  GetExtent(): Extent {
    return this.map.getView().calculateExtent(this.map.getSize())
  }

  GetLatLngExtent(): Extent {
      const extent = this.GetExtent()
      return transformExtent(extent, this.projection, latLngProjection)
  }

  GoToLatLngExtent(latLngExtent: Extent) {
    const extent = transformExtent(latLngExtent, latLngProjection, this.projection)
    this.GoToExtent(extent)
  }

  GoToExtent(extent: Extent) {
    const resolution = this.map.getView().getResolutionForExtent(extent)
    this.map.getView().setResolution(resolution)
    this.map.getView().setCenter(getCenter(extent))
  }

  SetResolutionForLatLngExtent(latLngExtent: Extent) {
      const extent = transformExtent(latLngExtent, latLngProjection, this.projection)
      const resolution = this.map.getView().getResolutionForExtent(extent)
      this.map.getView().setResolution(resolution)
  }

  // Calculate an Extent that includes all current tracks (ie all 
  // current track layers), or null if there are no tracks yet
  allTracksExtent(): Extent|null {
      let trackExt: Extent|null = null;
      this.map.getLayers().forEach( (layer) => {
          if (layer instanceof TrackVectorLayer) {
              const layerExt = layer.getSource().getExtent();
              if (layerExt[0] != Infinity) { // the extent is "infinity" if there are no features on the layer
                  if (trackExt) {
                      trackExt = extend(trackExt, layerExt)
                  } else {
                      trackExt = layerExt
                  }
              }
          }
      })
      return trackExt
  }
    
}